import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingDown, Lightbulb, ArrowLeft } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import FAQAccordion from '@/components/FAQAccordion';
import heropaars from '@/assets/heropaars.png';
import heropaarsWebp from '@/assets/heropaars.webp';
import organisatieVisual from '@/assets/systemique/organisatie-visual.webp';
import { SEO } from '@/components/SEO';
import { DEFAULT_OG_IMAGE, SITE_ORIGIN } from '@/seo/siteConfig';

// ORGANISATIEOPSTELLINGEN - VERNIEUWDE VERSIE
const Organisatieopstellingen = () => {
  const navigate = useNavigate();
  
  const handleNavigate = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title="Organisatieopstellingen Amsterdam, Haarlem & Noord-Holland"
        description="Organisatieopstellingen in Amsterdam, Haarlem, Castricum en heel Noord-Holland. Hoog verloop of stroeve samenwerking? Maak de onderstroom zichtbaar. Systemisch werk voor teams, managers en HR. Vraag offerte aan."
        keywords="organisatieopstellingen Amsterdam, organisatieopstellingen Haarlem, organisatieopstellingen Castricum, systemische teamcoaching Amsterdam, organisatieopstellingen Noord-Holland, organisatieopstellingen Zaanstreek, teamopstellingen Alkmaar, organisatieopstellingen bedrijven, systemisch werk organisaties Amsterdam, organisatieopstellingen HR, organisatieopstellingen hoog verloop, teamcoaching systemisch Zaandam"
        canonical="https://organisatieopstellingen.com/organisatieopstellingen"
        ogDescription="Organisatieopstellingen in Amsterdam, Haarlem en Noord-Holland. Hoog verloop of stroeve samenwerking? Maak de onderstroom zichtbaar met systemisch werk."
        ogImage={DEFAULT_OG_IMAGE}
      />
      
      {/* Service Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Organisatieopstellingen",
            "provider": {
              "@type": "Person",
              "name": "Marthe van der Molen",
              "url": `${SITE_ORIGIN}/over`,
              "email": "marthe@systemique.nl",
              "telephone": "+31-6-12991453"
            },
            "areaServed": [
              {"@type": "City", "name": "Amsterdam"},
              {"@type": "City", "name": "Haarlem"},
              {"@type": "City", "name": "Castricum"},
              {"@type": "City", "name": "Zaandam"},
              {"@type": "City", "name": "Alkmaar"},
              {"@type": "City", "name": "Uitgeest"},
              {"@type": "City", "name": "Hoofddorp"},
              {"@type": "City", "name": "Amstelveen"},
              {"@type": "City", "name": "Heerhugowaard"},
              {"@type": "AdministrativeArea", "name": "Zaanstreek"},
              {"@type": "State", "name": "Noord-Holland"}
            ],
            "description": "Organisatieopstellingen voor teams en organisaties. Systemische methode die patronen en blokkades zichtbaar maakt.",
            "audience": {
              "@type": "Audience",
              "audienceType": "Managers, HR-professionals, bestuurders en leidinggevenden"
            }
          })
        }}
      />
      
      {/* FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Wat zijn organisatieopstellingen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Organisatieopstellingen zijn een systemische methode waarbij organisatievraagstukken ruimtelijk worden weergegeven met representanten of symbolen. Het maakt onzichtbare patronen, verhoudingen en blokkades (de onderstroom) direct zichtbaar, zodat er beweging kan komen in wat vastzit."
                }
              },
              {
                "@type": "Question",
                "name": "Wanneer worden organisatieopstellingen ingezet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dit is effectief bij hoog verloop, terugkerende conflicten, gedoe of weerstand in teams, vastgelopen verandertrajecten, na fusies of overnames, bij leiderschapsvragen, of wanneer u merkt dat 'gewoon praten' of standaard coaching het probleem niet oplost. Vooral wanneer de dynamiek complex is en er onderhuidse spanning speelt."
                }
              },
              {
                "@type": "Question",
                "name": "Wat is het verschil met teamcoaching?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Teamcoaching richt zich vaak op gedrag en communicatie (de bovenstroom). Opstellingen werken in de diepere laag: gaat het over de juiste plek, ordening en balans in het systeem? We kijken naar de oorzaak, niet alleen naar het symptoom."
                }
              },
              {
                "@type": "Question",
                "name": "Hoelang duurt een organisatieopstelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Een organisatieopstelling duurt meestal 1,5 tot 2 uur per casus. Voor teamsessies plannen we vaak een dagdeel (4 uur) of een hele dag, afhankelijk van de vraag en de groepsgrootte."
                }
              }
            ]
          })
        }}
      />
      
      <div className="animate-fade-in">
        {/* Custom Luxury Header */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
          <div className="absolute inset-0 z-0">
            <GrainTexture opacity={0.2} />
            <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
          </div>
          
          <div className="container mx-auto relative z-10 text-center">
            <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
              <Link to="/">
                <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
              </Link>
            </Button>
            
            <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
              Organisatie<span className="italic font-light font-serif text-[#F9EBEF]">opstellingen</span>
              <span className="block text-xl md:text-2xl mt-4 font-serif italic text-[#F9EBEF]/80">
                Systemisch werk voor teams en organisaties
              </span>
            </h1>
          </div>
        </section>
        
        {/* Introductie met 'De Systemische Bril' - Nu prominenter */}
        <section className="py-12 md:py-20 bg-white relative">
          <div className="container mx-auto max-w-4xl px-4 sm:px-6 text-center">
            <Badge className="mb-4 md:mb-6">De Diepte In</Badge>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl text-[#6b4912] mb-6 md:mb-8 leading-tight px-2">
              Wat is een <span className="italic font-light font-serif text-[#854C73]">organisatieopstelling</span>?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed mb-6 md:mb-8 px-2">
              Een <strong>organisatieopstelling</strong> is een werkvorm binnen systemisch werk waarbij je vraagstuk
              ruimtelijk wordt uitgezet met representanten of symbolen. Het maakt zichtbaar wat vaak onbespreekbaar
              blijft: patronen, verhoudingen en blokkades in het systeem. Zo kun je gericht stappen zetten, zowel
              probleemoplossend als kansgericht.
            </p>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto mb-8 md:mb-12 text-left">
              {[
                { term: "Duur per casus", desc: "1,5 tot 2 uur" },
                { term: "Teamsessie", desc: "Dagdeel tot hele dag" },
                { term: "Regio", desc: "Castricum, NL-breed" },
                { term: "Begeleider", desc: "Marthe van der Molen" },
              ].map((item) => (
                <div key={item.term} className="bg-[#FAEBEE] rounded-xl p-4 border border-[#854C73]/10">
                  <dt className="text-xs uppercase tracking-wide text-[#854C73]/70 font-semibold mb-1">{item.term}</dt>
                  <dd className="text-sm md:text-base text-[#6b4912] font-medium">{item.desc}</dd>
                </div>
              ))}
            </dl>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 px-2 max-w-2xl mx-auto">
              Een organisatie is een levend systeem: wat op papier staat vertelt niet altijd het hele verhaal. De
              onderstroom, loyaliteiten en ordening bepalen vaak meer dan procedures alleen.
            </p>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 md:mb-12 px-2 max-w-2xl mx-auto">
              Werk je in Noord-Holland? Bekijk ons{' '}
              <Link
                to="/organisatieopstellingen-noord-holland"
                className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D] font-medium"
              >
                werkgebied organisatieopstellingen in Noord-Holland
              </Link>
              .
            </p>

            {/* Quote */}
            <div className="max-w-4xl mx-auto mt-8 md:mt-12 px-2 text-center">
              <span className="font-serif text-2xl sm:text-3xl md:text-5xl italic font-light text-[#854C73] leading-tight">
                "Zicht op de onderstroom: doorbreek impasses en ontgrendel het potentieel van jouw organisatie."
              </span>
            </div>
            
            <div className="h-1 w-24 bg-[#854C73]/20 mx-auto rounded-full mt-8 md:mt-12"></div>
          </div>
        </section>

        {/* Twee sporen: Probleem & Potentieel */}
        <section className="py-12 md:py-24 bg-[#FAEBEE] relative overflow-hidden px-4 sm:px-6">
          <GrainTexture opacity={0.15} />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#854C73]/20 to-transparent"></div>
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#6b4912] text-center mb-10 md:mb-16">
              Wanneer kies je voor een <span className="italic font-light font-serif text-[#854C73]">organisatieopstelling</span>?
            </h2>
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 lg:gap-20">
              {/* Kolom 1: Probleemgericht */}
              <div className="bg-white p-6 sm:p-8 md:p-10 rounded-2xl md:rounded-3xl shadow-xl border border-[#fff] hover:border-[#854C73]/20 transition-colors duration-300">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center mb-6 md:mb-8 text-[#854C73]">
                  <TrendingDown size={24} className="sm:w-7 sm:h-7" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#6b4912] mb-4 md:mb-6">Wanneer er een patroon vastzit</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-6 md:mb-8 italic">"Waarom herhaalt dit patroon zich steeds?"</p>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    "Mijn team zit in de weerstand",
                    "De samenwerking verloopt stroef of moeizaam",
                    "Er is veel uitval of verloop in de organisatie",
                    "Er is spanning tussen verschillende teamleden"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-gray-700">
                      <div className="mt-1.5 w-2 h-2 rounded-full bg-[#854C73] shrink-0"></div>
                      <span className="text-base sm:text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Kolom 2: Potentieelgericht */}
              <div className="bg-[#854C73] p-6 sm:p-8 md:p-10 rounded-2xl md:rounded-3xl shadow-xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/10 rounded-full flex items-center justify-center mb-6 md:mb-8 text-white">
                  <Lightbulb size={24} className="sm:w-7 sm:h-7" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 md:mb-6">Wanneer je richting wilt geven</h3>
                <p className="text-sm sm:text-base text-white/80 mb-6 md:mb-8 italic">"Hoe geven we de toekomst de beste plek?"</p>
                <ul className="space-y-3 md:space-y-4">
                  {[
                    "Een strategische keuze maken voor mijn organisatie",
                    "De juiste plek vinden voor nieuwe medewerkers",
                    "De fusie of overname ondersteunen",
                    "De koers van de afdeling verhelderen"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 sm:gap-4 text-white/90">
                      <div className="mt-1.5 w-2 h-2 rounded-full bg-white shrink-0"></div>
                      <span className="text-base sm:text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Werkwijze / Resultaat */}
        <section className="py-12 md:py-24 bg-white px-4 sm:px-6">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-5 order-2 md:order-1">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#854C73] rounded-2xl opacity-10"></div>
                  <OptimizedImage 
                    src={organisatieVisual} 
                    webpSrc={organisatieVisual}
                    className="rounded-2xl shadow-lg relative z-10 w-full h-auto" 
                    alt="organisatieopstellingen sessie bedrijf" 
                    width={1067}
                    height={1600}
                  />
                </div>
              </div>
              <div className="md:col-span-7 space-y-6 md:space-y-8 order-1 md:order-2">
                <h2 className="font-serif text-2xl sm:text-3xl text-[#6b4912]">Wat levert een <span className="italic font-light font-serif">Organisatieopstelling</span> mij op?</h2>
                <div className="space-y-4 md:space-y-6">
                  <div className="flex gap-4 sm:gap-6">
                    <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FAEBEE] flex items-center justify-center text-[#854C73] font-serif font-bold text-lg sm:text-xl">1</div>
                    <div>
                      <h4 className="font-bold text-[#854C73] text-lg sm:text-xl mb-2">Zichtbaar wat verborgen was</h4>
                      <p className="text-sm sm:text-base text-gray-600">Patronen, verhoudingen en blokkades komen letterlijk in beeld. Je ziet direct waar de energie stroomt en waar het stagneert.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 sm:gap-6">
                    <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FAEBEE] flex items-center justify-center text-[#854C73] font-serif font-bold text-lg sm:text-xl">2</div>
                    <div>
                      <h4 className="font-bold text-[#854C73] text-lg sm:text-xl mb-2">Ervaren in plaats van bedenken</h4>
                      <p className="text-sm sm:text-base text-gray-600">Omdat we werken met representanten en ruimtelijke opstellingen, wordt de informatie voelbaar. Dit beklijft dieper dan een cognitieve analyse.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 sm:gap-6">
                    <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FAEBEE] flex items-center justify-center text-[#854C73] font-serif font-bold text-lg sm:text-xl">3</div>
                    <div>
                      <h4 className="font-bold text-[#854C73] text-lg sm:text-xl mb-2">Beweging richting oplossing</h4>
                      <p className="text-sm sm:text-base text-gray-600">We testen interventies direct in de opstelling. Wat gebeurt er als de leider een stap terug doet? Wat als het doel centraal staat? Je gaat naar huis met een concreet handelingsperspectief.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote / CTA Block */}
        <section className="py-12 md:py-20 px-4 sm:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-[#6b4912] rounded-2xl md:rounded-[3rem] p-8 sm:p-12 md:p-16 text-center text-[#FAEBEE] relative overflow-hidden">
              <GeoPattern />
              <div className="relative z-10">
                <h3 className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-6 md:mb-8 leading-tight px-2">"Organisatieopstellingen zijn vaak onderdeel van een groter ontwikkeltraject"</h3>
                <p className="text-white/80 text-sm sm:text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto px-2">
                  We onderzoeken samen het vraagstuk en kijken of een organisatieopstelling een passende interventie is
                </p>
                
                {/* Buttons onder elkaar */}
                <div className="flex flex-col gap-4 items-center mb-8">
                <Button variant="white" onClick={() => handleNavigate('/contact')} className="px-6 sm:px-8 md:px-10 py-3 md:py-4 text-sm sm:text-base md:text-lg w-full sm:w-auto">
                  Vraag een offerte aan
                </Button>
                  <Button 
                    asChild
                    variant="white" 
                    className="px-6 sm:px-8 md:px-10 py-3 md:py-4 text-sm sm:text-base md:text-lg w-full sm:w-auto"
                  >
                    <Link to="/agenda">Bekijk de agenda</Link>
                  </Button>
                </div>
                
                {/* Tekst onder de buttons */}
                <div className="max-w-2xl mx-auto">
                  <h4 className="font-serif text-xl sm:text-2xl text-[#FAEBEE] mb-4">
                    Wil je dit zelf <span className="italic font-light font-serif">ervaren</span>?
                  </h4>
                  <p className="text-white/70 text-sm sm:text-base md:text-lg">
                    Organisatieopstellingen zijn regelmatig onderdeel van onze opstellingenmiddagen. 
                    Bekijk de volgende data in <Link to="/agenda" className="text-[#FAEBEE] hover:underline font-semibold">mijn agenda</Link> en schrijf je in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <div className="px-4 sm:px-6">
          <FAQAccordion />
        </div>
      </div>
    </>
  );
};

export default Organisatieopstellingen;
